import React, { memo } from 'react';
import { Box, Typography, Chip, alpha, useTheme } from '@mui/material';
import {
  Router,
  Hub,
  Dns,
  Wifi,
  SettingsInputAntenna,
  Person,
  CloudQueue,
  Storage,
  DeviceHub,
} from '@mui/icons-material';
import type { Node } from '@antv/x6';
import { DispositivoRedeTipo } from '../../../services/dispositivoRedeService';
import { STATUS_COLORS, getMonitorLatenciaExibicao } from './monitoramentoRedeUtils';
import type { DiagramNodeData } from './diagramaRedeUtils';
import { getNodeIpExibicao, getTipoDiagramaLabel } from './diagramaRedeUtils';

const TIPO_ICONS: Record<string, React.ReactElement> = {
  switch: <Hub sx={{ fontSize: 18 }} />,
  servidor: <Dns sx={{ fontSize: 18 }} />,
  roteador: <Router sx={{ fontSize: 18 }} />,
  radio_ap: <Wifi sx={{ fontSize: 18 }} />,
  radio_station: <SettingsInputAntenna sx={{ fontSize: 18 }} />,
  cliente: <Person sx={{ fontSize: 18 }} />,
    servicos: <CloudQueue sx={{ fontSize: 18 }} />,
    energia: <CloudQueue sx={{ fontSize: 18 }} />,
    olt: <DeviceHub sx={{ fontSize: 18 }} />,
  nas: <Storage sx={{ fontSize: 18 }} />,
};

const STATUS_LABELS = {
  online: 'Online',
  offline: 'Offline',
  unknown: 'Desconhecido',
} as const;

interface DispositivoRedeGraphNodeProps {
  node: Node;
}

const DispositivoRedeGraphNode: React.FC<DispositivoRedeGraphNodeProps> = ({ node }) => {
  const theme = useTheme();
  const data = node.getData<DiagramNodeData>();
  const diagramNode = data?.node;

  if (!diagramNode) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Nó inválido
        </Typography>
      </Box>
    );
  }

  const statusColor = STATUS_COLORS[diagramNode.monitor_status];
  const tipoLabel = getTipoDiagramaLabel(diagramNode.tipo_label, diagramNode.nativo);
  const deviceTipo = diagramNode.dispositivo?.tipo ?? diagramNode.tipo_label;
  const latenciaLabel = getMonitorLatenciaExibicao(
    deviceTipo,
    diagramNode.dispositivo?.monitor_latencia_ms
  );
  const statusLabel = latenciaLabel
    ? `${STATUS_LABELS[diagramNode.monitor_status]} · ${latenciaLabel}`
    : STATUS_LABELS[diagramNode.monitor_status];
  const icon =
    TIPO_ICONS[diagramNode.tipo_label] ??
    TIPO_ICONS[(diagramNode.dispositivo?.tipo as DispositivoRedeTipo) ?? 'switch'];

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        px: 1.2,
        py: 1,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.4,
        bgcolor: alpha(statusColor, theme.palette.mode === 'dark' ? 0.22 : 0.14),
        border: `2px solid ${statusColor}`,
        boxShadow: theme.shadows[2],
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(statusColor, 0.12),
            color: statusColor,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: 1, fontSize: '0.78rem' }}>
          {diagramNode.nome}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
        <Chip
          size="small"
          label={tipoLabel}
          variant="outlined"
          sx={{ height: 20, fontSize: '0.62rem', maxWidth: '58%' }}
        />
        <Chip
          size="small"
          label={statusLabel}
          sx={{
            height: 20,
            fontSize: '0.62rem',
            bgcolor: alpha(statusColor, 0.14),
            color: statusColor,
            fontWeight: 700,
            maxWidth: latenciaLabel ? '62%' : undefined,
          }}
        />
      </Box>
    </Box>
  );
};

export default memo(DispositivoRedeGraphNode, (prev, next) => {
  if (!prev.node || !next.node) return false;
  return !next.node.hasChanged('data');
});
